import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { HealthState } from '../hooks/usePending.js'

interface Props {
  health: HealthState
  dappCount: number
  pendingRequestCount: number
  lastRefreshed: Date | null
}

export function SummaryCard({
  health,
  dappCount,
  pendingRequestCount,
  lastRefreshed,
}: Props) {
  const pending = health.kind === 'healthy' ? health.pending : null

  return (
    <Card className="mb-4">
      <CardContent>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            label="Pending tx"
            value={pending !== null ? String(pending) : '—'}
            tone={pending && pending > 0 ? 'blue' : 'muted'}
          />
          <Stat
            label="Connected dApps"
            value={String(dappCount)}
            tone={dappCount > 0 ? 'green' : 'muted'}
          />
          <Stat
            label="Live requests"
            value={String(pendingRequestCount)}
            tone={pendingRequestCount > 0 ? 'red' : 'muted'}
          />
          <Stat
            label="Last refreshed"
            value={lastRefreshed?.toLocaleTimeString() ?? '—'}
            tone="muted"
          />
        </div>
      </CardContent>
    </Card>
  )
}

const TONE_CLASSES: Record<'blue' | 'green' | 'red' | 'muted', string> = {
  blue: 'text-sky-400',
  green: 'text-emerald-400',
  red: 'text-destructive',
  muted: 'text-foreground',
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone: 'blue' | 'green' | 'red' | 'muted'
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          'text-2xl font-semibold tabular-nums',
          TONE_CLASSES[tone],
        )}
      >
        {value}
      </div>
    </div>
  )
}
